import posthog from "posthog-js"
import { config } from "/app.config"

export const POSTHOG_KEY = process.env.VUE_APP_POSTHOG_PROJECT_TOKEN
export const POSTHOG_HOST = process.env.VUE_APP_POSTHOG_HOST || "https://us.i.posthog.com"

/** Must match zeno.fm PricingPlans / PostHog experiment key exactly. */
export const PRICING_EXPERIMENT_FLAG = "pricing-monthly-vs-yearly-trial"
const VARIANT_STORAGE_KEY = "ph_pricing_experiment_variant"
/** Sticky QA arm from ?arm= / ?forcePlan=1 — survives Keycloak/login redirects if query is lost. */
const QA_ARM_STORAGE_KEY = "ph_pricing_experiment_qa"

/** Experiment metrics only — anything else is dropped at capture. */
const ALLOWED_EVENTS = new Set(["plan_cta_clicked", "paid_plan_created"])

const BOOTSTRAP_COOKIE = "ph_bootstrap"

export const isPostHogEnabled = () => {
  return Boolean(POSTHOG_KEY && POSTHOG_KEY.length && config.isZeno)
}

/**
 * Review overrides from URL (preferred) or sessionStorage.
 * `?arm=control|test` and `?forcePlan=1` (→ test). `?arm=clear` removes the sticky override.
 * @param {Record<string, string>|undefined} routeQuery optional vue-router query
 * @returns {'control'|'test'|null}
 */
export const getQueryArmOverride = (routeQuery) => {
  let arm = null
  let forcePlan = null

  try {
    const params = new URLSearchParams(window.location.search)
    arm = params.get("arm")
    forcePlan = params.get("forcePlan")
  } catch (e) {
    // URL parsing failed
  }

  // vue-router query as backup (and when search is empty after some redirects)
  if (routeQuery) {
    if (!arm && routeQuery.arm) {
      arm = Array.isArray(routeQuery.arm) ? routeQuery.arm[0] : routeQuery.arm
    }
    if (!forcePlan && routeQuery.forcePlan != null) {
      forcePlan = Array.isArray(routeQuery.forcePlan) ? routeQuery.forcePlan[0] : String(routeQuery.forcePlan)
    }
  }

  if (arm === "clear") {
    try {
      sessionStorage.removeItem(QA_ARM_STORAGE_KEY)
      sessionStorage.removeItem(VARIANT_STORAGE_KEY)
    } catch (e) {
      // ignore
    }
    return null
  }

  if (arm === "control" || arm === "test") {
    try {
      sessionStorage.setItem(QA_ARM_STORAGE_KEY, arm)
      // Force product cache to match QA so nothing else "listens" to a stale PostHog arm
      sessionStorage.setItem(VARIANT_STORAGE_KEY, arm)
    } catch (e) {
      // ignore
    }
    return arm
  }

  if (forcePlan === "1") {
    try {
      sessionStorage.setItem(QA_ARM_STORAGE_KEY, "test")
      sessionStorage.setItem(VARIANT_STORAGE_KEY, "test")
    } catch (e) {
      // ignore
    }
    return "test"
  }

  try {
    const sticky = sessionStorage.getItem(QA_ARM_STORAGE_KEY)
    if (sticky === "control" || sticky === "test") {
      return sticky
    }
  } catch (e) {
    // sessionStorage unavailable
  }

  return null
}

const clearBootstrapCookie = () => {
  // Clear both host-only and shared .zeno.fm variants.
  document.cookie = `${BOOTSTRAP_COOKIE}=; path=/; max-age=0`
  document.cookie = `${BOOTSTRAP_COOKIE}=; domain=.zeno.fm; path=/; max-age=0`
}

const parseBootstrapPayload = (raw) => {
  if (!raw) {
    return null
  }

  try {
    // Accept both URI-encoded payloads and plain JSON (Nitro setCookie).
    let decoded = raw
    try {
      decoded = decodeURIComponent(raw)
    } catch (e) {
      // already decoded
    }

    const data = JSON.parse(decoded)
    const distinctID = data.distinctID || data.distinct_id
    const sessionID = data.sessionID || data.session_id

    if (!distinctID) {
      return null
    }

    return {
      distinctID,
      ...(sessionID ? { sessionID } : {}),
    }
  } catch (e) {
    return null
  }
}

const readBootstrapFromCookie = () => {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${BOOTSTRAP_COOKIE}=([^;]*)`))
    if (!match) {
      return null
    }

    const bootstrap = parseBootstrapPayload(match[1])
    clearBootstrapCookie()
    return bootstrap
  } catch (e) {
    return null
  }
}

const readBootstrapFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search)
    const distinctID = params.get("ph_distinct_id")
    const sessionID = params.get("ph_session_id")

    if (!distinctID) {
      return null
    }

    return {
      distinctID,
      ...(sessionID ? { sessionID } : {}),
    }
  } catch (e) {
    return null
  }
}

/**
 * Init PostHog once (Zeno brand only). Prefer URL bootstrap IDs from the marketing
 * site, then the cross-subdomain cookie handoff, so pricing-experiment persons stay linked.
 */
export const initPostHog = () => {
  if (!isPostHogEnabled()) {
    return null
  }

  if (window.__posthogInitialized) {
    return posthog
  }

  const bootstrapIds = readBootstrapFromUrl() || readBootstrapFromCookie() || {}
  const bootstrap = {}

  if (bootstrapIds.distinctID) {
    bootstrap.distinctID = bootstrapIds.distinctID
  }
  if (bootstrapIds.sessionID) {
    bootstrap.sessionID = bootstrapIds.sessionID
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: "https://us.posthog.com",
    persistence: "localStorage+cookie",
    // Experiment arm + the two metrics only — no auto instrumentation noise.
    capture_pageview: false,
    capture_pageleave: false,
    autocapture: false,
    disable_session_recording: true,
    ...(Object.keys(bootstrap).length ? { bootstrap } : {}),
  })

  // Cache experiment assignment while still on the bootstrapped (marketing) distinct_id,
  // before Keycloak identify() can change the id used for flag hashing.
  // send_event: false — avoid tools-side $feature_flag_called; marketing owns exposure if used.
  posthog.onFeatureFlags(() => {
    persistPricingExperimentVariant(
      posthog.getFeatureFlag(PRICING_EXPERIMENT_FLAG, { send_event: false })
    )
  })

  window.__posthogInitialized = true
  return posthog
}

export const identifyPostHogUser = (user) => {
  if (!isPostHogEnabled() || !user?.id) {
    return
  }

  try {
    posthog.identify(String(user.id), {
      email: user.email,
      first_name: user.basic_info?.first_name,
      last_name: user.basic_info?.last_name,
    })
  } catch (e) {
    console.log("[PostHog] identify error: ", e)
  }
}

export const trackPostHogEvent = (eventName, properties = {}) => {
  try {
    if (!isPostHogEnabled()) {
      return
    }

    if (!ALLOWED_EVENTS.has(eventName)) {
      return
    }

    posthog.capture(eventName, properties)
  } catch (e) {
    console.log("[PostHog] Error: ", e)
  }
}

/**
 * Pricing experiment variant for the current person.
 * 1) QA URL/session override (always wins — ignores flag)
 * 2) Live PostHog flag when loaded
 * 3) Session sticky only while flags are still loading (tracking / race safety)
 * 4) null when flag is off / not assigned (and clears stale session arm)
 * @returns {'control'|'test'|null}
 */
export const getPricingExperimentVariant = (routeQuery) => {
  const qa = getQueryArmOverride(routeQuery)
  if (qa) {
    return qa
  }

  if (!isPostHogEnabled()) {
    return null
  }

  try {
    // Wait for a real /decide response when available. Painting UI from sticky "test"
    // then flipping to flag-off null caused trial flash — so session is only a bridge
    // until /decide lands, not a permanent override.
    if (posthog.featureFlags && posthog.featureFlags.hasLoadedFlags) {
      const live = posthog.getFeatureFlag(PRICING_EXPERIMENT_FLAG, { send_event: false })
      if (live === "control" || live === "test") {
        try {
          sessionStorage.setItem(VARIANT_STORAGE_KEY, live)
        } catch (e) {
          // ignore
        }
        return live
      }
      // Flag inactive or not multivariate for this user — drop stale sticky arm
      try {
        sessionStorage.removeItem(VARIANT_STORAGE_KEY)
      } catch (e) {
        // ignore
      }
      return null
    }

    // Flags not loaded yet — sticky arm from earlier decide (bootstrap / prior page)
    try {
      const sticky = sessionStorage.getItem(VARIANT_STORAGE_KEY)
      if (sticky === "control" || sticky === "test") {
        return sticky
      }
    } catch (e) {
      // sessionStorage unavailable
    }
  } catch (e) {
    console.log("[PostHog] getFeatureFlag error: ", e)
  }

  return null
}

/**
 * Shared props so plan CTA + paid conversion join cleanly in PostHog.
 * Dual-writes `variant` (matches marketing plan CTA taxonomy) + `experiment_variant`.
 * Unassigned / flag-off → variant "baseline" (marketing holdout name), experiment_variant null.
 * @param {'control'|'test'|null|undefined} armOverride optional arm already applied to the plan UI
 * @returns {Record<string, string|null>}
 */
export const getPricingExperimentEventProps = (armOverride, routeQuery) => {
  const arm =
    armOverride === "control" || armOverride === "test"
      ? armOverride
      : getPricingExperimentVariant(routeQuery)

  return {
    experiment: PRICING_EXPERIMENT_FLAG,
    // Primary tools property (always control | test | null)
    experiment_variant: arm,
    // Marketing site uses `variant` on plan_cta_clicked — keep both so one breakdown works
    variant: arm || "baseline",
  }
}

const persistPricingExperimentVariant = (variant) => {
  if (variant !== "control" && variant !== "test") {
    return
  }

  // Never let background flag callbacks overwrite an active QA arm
  if (getQueryArmOverride()) {
    return
  }

  try {
    sessionStorage.setItem(VARIANT_STORAGE_KEY, variant)
  } catch (e) {
    // sessionStorage unavailable
  }
}

/**
 * Call `callback(variant)` now and whenever flags reload.
 * With QA override: fires once and never listens to PostHog flags.
 * @returns {Function} unsubscribe
 */
export const onPricingExperimentReady = (callback, routeQuery) => {
  const qa = getQueryArmOverride(routeQuery)
  if (qa) {
    callback(qa)
    return () => {}
  }

  if (!isPostHogEnabled()) {
    callback(null)
    return () => {}
  }

  const apply = () => {
    // Always re-check QA in case user navigates with ?arm while mounted
    callback(getPricingExperimentVariant(routeQuery))
  }

  if (posthog.featureFlags && typeof posthog.featureFlags.hasLoadedFlags === "boolean") {
    if (posthog.featureFlags.hasLoadedFlags) {
      apply()
    }
  }

  return posthog.onFeatureFlags(apply)
}
